import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import ExamEditor from './components/ExamEditor';
import { Container, Loading, ErrorMessage } from '@/src/ui';
import type { QtiExamDTO } from './models/ExamDTOs';
import { apiRequest } from '@/src/services';

const CreateEditExamPage: React.FC = () => {
    const [initialModel, setInitialModel] = useState<QtiExamDTO | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [searchParams] = useSearchParams();
    const viewMode = (searchParams.get('viewMode') as 'create' | 'edit') || 'create';
    const resourceId = searchParams.get('resourceId')
        ? Number(searchParams.get('resourceId'))
        : undefined;

    useEffect(() => {
        document.title = viewMode === 'create' ? 'Create Exam' : 'Edit Exam';
    }, [viewMode]);

    useEffect(() => {
        if (viewMode === 'edit' && resourceId) {
            // Fetch the exam model by resourceId
            (async function fetchExam() {
                setLoading(true);
                try {
                    const res = await apiRequest<QtiExamDTO>(
                        'get',
                        `/assessments/exams/${resourceId}`
                    );
                    setInitialModel(res.data);
                } catch {
                    setError('Failed to fetch exam model');
                } finally {
                    setLoading(false);
                }
            })();
        } else {
            setInitialModel(undefined); // Reset for create mode
            setLoading(false);
        }
    }, [viewMode, resourceId]);

    return (
        <Container>
            <h1 className="page-header">{viewMode === 'create' ? 'Create Exam' : 'Edit Exam'}</h1>

            {loading && <Loading size="lg" />}
            <ErrorMessage error={error} />

            {!loading && <ExamEditor initialModel={initialModel} />}
        </Container>
    );
};

export default CreateEditExamPage;
